// src/components/rooms/AddRoomModal.js

import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
// Styles are imported in PropertyRoomsTab, so classes are available here too 
// or you can import them here as well to be safe:
import '../../styles/RoomsScreen.css';

const AddRoomModal = ({ 
    isOpen, 
    onClose, 
    onSave, 
    existingRooms = [],
    roomTypes = [], 
    floorOptions = [],
    roomToEdit = null 
}) => {
    const [formData, setFormData] = useState({
        number: '',
        type: '', 
        floor: ''
    });
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (roomToEdit) {
                setFormData({
                    number: roomToEdit.number,
                    type: roomToEdit.type,
                    floor: roomToEdit.floor
                });
            } else {
                setFormData({
                    number: '',
                    type: roomTypes[0] || '',
                    floor: floorOptions[0] || ''
                });
            }
            setError('');
        }
    }, [isOpen, roomToEdit, roomTypes, floorOptions]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.number.trim()) {
            setError('Please enter a room number.');
            return;
        }

        if (!formData.type || !formData.floor) {
            setError('Please select both a Room Type and a Floor.');
            return;
        }

        const isDuplicate = existingRooms.some(room => 
            room.number.toLowerCase() === formData.number.trim().toLowerCase() &&
            (roomToEdit ? room.id !== roomToEdit.id : true)
        );

        if (isDuplicate) {
            setError(`Room ${formData.number} already exists.`);
            return;
        }

        onSave({ 
            ...formData, 
            id: roomToEdit ? roomToEdit.id : undefined 
        });
    };

    return (
        <div className="room-modal-backdrop">
            <div className="room-modal-content">
                <div className="room-modal-header">
                    <h3 className="room-modal-title">
                        {roomToEdit ? 'Edit Room' : 'Add New Room'}
                    </h3>
                    <button className="room-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="room-modal-body">
                    {error && (
                        <div className="room-error-message">
                            {error}
                        </div>
                    )}

                    <div className="room-form-group">
                        <label className="room-form-label">Room Number</label>
                        <input
                            type="text"
                            name="number"
                            className="room-form-input"
                            placeholder="e.g. 101"
                            value={formData.number}
                            onChange={handleChange}
                            autoFocus
                        />
                    </div>

                    <div className="room-form-group">
                        <label className="room-form-label">Floor</label>
                        <select
                            name="floor"
                            className="room-form-select"
                            value={formData.floor}
                            onChange={handleChange}
                        >
                            {floorOptions.map(floor => (
                                <option key={floor} value={floor}>{floor}</option>
                            ))}
                        </select>
                    </div>

                    <div className="room-form-group">
                        <label className="room-form-label">Room Type</label>
                        <select
                            name="type"
                            className="room-form-select"
                            value={formData.type}
                            onChange={handleChange}
                        >
                            {roomTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className="room-modal-actions">
                        <button type="button" className="button button-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="button action-button">
                            <Save size={16} style={{ marginRight: '8px' }} />
                            {roomToEdit ? 'Save Changes' : 'Create Room'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddRoomModal;